<!-- Responsibility: Coordinate world and vessel forging.
Scope: Owns forging draft, AI forge triggers, and initial game launch. -->
<forging-view class="menu-overlay">
  <div class="menu-section active">
    <p if={ state.importedPlayer || state.importedSeed } class="wizard-import-note">
        IMPORT READY:
        <span if={ state.importedSeed }> WORLD</span>
        <span if={ state.importedPlayer }> VESSEL</span>
      </p>

      <form ref="wizardForm" class="wizard-form autoform wizard { state.showErrors ? 'show-errors' : '' }" onsubmit={ startNewGame }>
        
        <!-- Step Indicators -->
        <div class="wizard-steps">
          <div class="wizard-step-item { state.step === 0 ? 'active' : '' } { state.step > 0 ? 'done' : '' }">
            <span class="step-num">1</span>
            <span class="step-label">WORLD</span>
          </div>
          <div class="wizard-step-sep"></div>
          <div class="wizard-step-item { state.step === 1 ? 'active' : '' }">
            <span class="step-num">2</span>
            <span class="step-label">VESSEL</span>
          </div>
        </div>

        <div class="wizard-body">
          <div if={ state.step === 0 } ref="worldStep">
            <world-source-surface
              view={ worldSourceView() }
              actions={ worldSourceActions() }
              draft={ state.draftSnapshot }
            ></world-source-surface>
          </div>

          <div if={ state.step === 1 } ref="vesselStep">
            <vessel-definition-surface
              view={ vesselView() }
              actions={ vesselActions() }
              draft={ state.draftSnapshot }
            ></vessel-definition-surface>
          </div>
        </div>

        <p if={ state.error } class="wizard-error-note">{ state.error }</p>
        
        <div class="wizard-footer-nav">
          <button if={ state.step === 0 } type="button" class="action-btn" onclick={ backToLobby }>← LOBBY</button>
          <button if={ state.step > 0 } type="button" class="action-btn" onclick={ prevStep }>← PREVIOUS</button>
          
          <button if={ state.step === 0 } type="button" class="action-btn primary" onclick={ nextStep }>NEXT: VESSEL →</button>
          
          <button
            if={ state.step === 1 }
            type="submit"
            class="action-btn primary wizard-submit"
            disabled={ state.forging || state.selectingWorldId }
          >
            <span>{ state.forging ? 'FORGING...' : forgeLabel() }</span>
          </button>
        </div>

        <div class="wizard-actions-footer">
          <a class="nav-link danger-link" onclick={ cancel }>CLEAR DRAFT</a>
        </div>
      </form>
    </div>
  </div>

  <script>
    import { collectValues, populateFields } from 'field-assist'
    import * as gameStore from '../../game-store.js'
    import { navigateState, sysNotify } from '../../app-states.js'
    import { formatAIErrorMessage } from '../../ai/errors.js'
    import { normalizeSex } from '../../engine/state.js'
    import { persistenceService } from '../../services/persistence-service.ts'
    import VesselDefinitionSurface from '../components/wizard/vessel-definition-surface.riot'
    import WorldSourceSurface from '../components/wizard/world-source-surface.riot'
    import { WorldDefinitionModel } from '../wizard/world-definition-model.ts'
    import { playEffect } from '../../engine/audio.js'

    const NEW_GAME_DRAFT_KEY = 'am2_new_game_draft'

    export default {
      components: { WorldSourceSurface, VesselDefinitionSurface },
      state: {
        step:         0,
        forging:      false,
        error:        null,
        importedSeed: (gameStore.importedWorldData || {}).seed || null,
        importedPlayer: gameStore.importedPlayerData || null,
        worldGallery: [],
        galleryLoading: false,
        galleryError: null,
        selectedWorldSaveId: null,
        selectingWorldId: null,
        worldDefinitionOpen: false,
        activeSurface: 'world',
        worldSummary: 'World inspiration not defined yet.',
        vesselSummary: 'Vessel details not defined yet.',
        draftSnapshot: {
          name: '',
          identity: '',
          lineage: '',
          sex: '',
          bio: '',
          world: { inspiration: '', modifiers: '', narrativeType: '' },
        },
      },

      onMounted() {
        this.restoreDraft()
        this.syncImportedSetup()
        this.loadWorldGallery()
        this.bindDraftPersistence()
        requestAnimationFrame(() => this.refreshComposerState())
      },

      onUnmounted() {
        this.draftInputCleanup?.()
      },

      bindDraftPersistence() {
        const syncVisibleDraft = () => {
          return this.captureDraftSnapshot(this.readDraftFormValues())
        }
        const persistNow = () => {
          clearTimeout(this.debounceTimer)
          const draft = syncVisibleDraft()
          this.refreshComposerState()
          this.update()
          this.persistDraft(draft)
        }
        const persist = () => {
          const draft = syncVisibleDraft()
          clearTimeout(this.debounceTimer)
          this.debounceTimer = setTimeout(() => {
            this.persistDraft(draft)
          }, 300)
        }
        this.root?.addEventListener('input', persist)
        this.root?.addEventListener('change', persist)
        window.addEventListener('pagehide', persistNow)
        this.draftInputCleanup = () => {
          clearTimeout(this.debounceTimer)
          this.root?.removeEventListener('input', persist)
          this.root?.removeEventListener('change', persist)
          window.removeEventListener('pagehide', persistNow)
        }
      },

      getFormNode() {
        return this.$('[ref="wizardForm"]')
      },

      readDraftFormValues() {
        const form = this.getFormNode()
        if (!form) return this.state.draftSnapshot
        const values = collectValues(form, undefined, 'name')
        delete values._errors
        return values
      },

      setDraftSnapshot(snapshot = {}) {
        const current = this.latestDraftSnapshot || this.state.draftSnapshot || {}
        const world = snapshot?.world || {}
        const clean = (val, fallback = '') => typeof val === 'symbol' ? fallback : val
        const allowWorldDraft = this.state.step === 0
        const allowVesselDraft = this.state.step === 1
        const pick = (incoming, fallback, allowIncoming) => {
          const source = allowIncoming ? incoming : fallback
          return typeof source === 'symbol' ? fallback : source
        }
        const merged = {
          name: String(clean(pick(snapshot?.name, current?.name, allowVesselDraft), current?.name) ?? '').trim(),
          identity: String(clean(pick(snapshot?.identity, current?.identity, allowVesselDraft), current?.identity) ?? '').trim(),
          lineage: String(clean(pick(snapshot?.lineage, current?.lineage, allowVesselDraft), current?.lineage) ?? '').trim(),
          sex: String(clean(pick(snapshot?.sex, current?.sex, allowVesselDraft), current?.sex) ?? '').trim(),
          bio: String(clean(pick(snapshot?.bio, current?.bio, allowVesselDraft), current?.bio) ?? '').trim(),
          world: {
            inspiration: String(clean(pick(world?.inspiration ?? snapshot?.['world.inspiration'], current?.world?.inspiration, allowWorldDraft), current?.world?.inspiration) ?? '').trim(),
            modifiers: String(clean(pick(world?.modifiers ?? snapshot?.['world.modifiers'], current?.world?.modifiers, allowWorldDraft), current?.world?.modifiers) ?? '').trim(),
            narrativeType: String(clean(pick(world?.narrativeType ?? snapshot?.['world.narrativeType'], current?.world?.narrativeType, allowWorldDraft), current?.world?.narrativeType) ?? '').trim(),
          }
        }
        this.latestDraftSnapshot = merged
        this.update({ draftSnapshot: merged })
        return merged
      },

      captureDraftSnapshot(snapshot = {}) {
        return this.setDraftSnapshot(snapshot)
      },

      applyDraftSnapshotToForm(snapshot = this.state.draftSnapshot) {
        const form = this.getFormNode()
        if (!form) return
        // Flatten nested world for field-assist
        const flat = {
          ...snapshot,
          'world.inspiration': snapshot?.world?.inspiration,
          'world.modifiers': snapshot?.world?.modifiers,
          'world.narrativeType': snapshot?.world?.narrativeType,
        }
        populateFields(form, flat, 'name')
      },

      persistDraft(snapshot = this.latestDraftSnapshot || this.state.draftSnapshot || {}, step = this.state.step) {
        const merged = snapshot || this.latestDraftSnapshot || this.state.draftSnapshot || {}
        const payload = {
          ...merged,
          selectedWorldSaveId: this.state.selectedWorldSaveId ?? null,
          importedWorldData: gameStore.importedWorldData ?? null,
          importedPlayerData: gameStore.importedPlayerData ?? null,
          worldDefinitionOpen: this.state.worldDefinitionOpen,
          step,
        }
        try {
          sessionStorage.setItem(NEW_GAME_DRAFT_KEY, JSON.stringify(payload))
        } catch {
          // ignored
        }
      },

      clearDraft() {
        try {
          sessionStorage.removeItem(NEW_GAME_DRAFT_KEY)
        } catch {
          // ignored
        }
      },

      restoreDraft() {
        try {
          const raw = sessionStorage.getItem(NEW_GAME_DRAFT_KEY)
          if (!raw) return
          const draft = JSON.parse(raw)
          if (!draft || typeof draft !== 'object' || Array.isArray(draft)) throw new Error('Invalid draft shape')
          
          gameStore.setImportedWorldData(draft?.importedWorldData ?? null)
          gameStore.setImportedPlayerData(draft?.importedPlayerData ?? null)
          this.latestDraftSnapshot = draft
          
          this.update({
            draftSnapshot: draft,
            selectedWorldSaveId: draft?.selectedWorldSaveId ?? null,
            worldDefinitionOpen: draft?.worldDefinitionOpen ?? (!draft?.importedWorldData?.seed && draft?.world?.inspiration),
            step: draft?.step ?? (draft?.importedWorldData?.seed ? 1 : 0),
          })
          requestAnimationFrame(() => {
            this.applyDraftSnapshotToForm(draft)
            this.refreshComposerState()
          })
        } catch {
          // ignored
        }
      },

      syncImportedSetup() {
        const importedSeed = (gameStore.importedWorldData || {}).seed || null
        const importedPlayer = gameStore.importedPlayerData || null
        this.update({
          importedSeed,
          importedPlayer,
          worldDefinitionOpen: importedSeed ? false : this.state.worldDefinitionOpen,
          step: importedSeed ? 1 : this.state.step,
        })
        if (!importedPlayer) return
        
        const current = this.state.draftSnapshot
        const hydrated = {
          ...current,
          identity: current.identity || importedPlayer.identity || '',
          sex: current.sex || normalizeSex(importedPlayer.sex),
          bio: current.bio || importedPlayer.bio || '',
        }
        
        this.latestDraftSnapshot = hydrated
        this.update({ draftSnapshot: hydrated })
        requestAnimationFrame(() => {
          this.applyDraftSnapshotToForm(hydrated)
          this.refreshComposerState()
        })
      },

      nextStep() {
        const worldStepNode = this.$('[ref="worldStep"]')
        if (worldStepNode) {
          const values = collectValues(worldStepNode, undefined, 'name')
          if (values._errors && Object.keys(values._errors).length > 0) {
            this.update({ error: 'Please define your world inspiration first.' })
            return
          }
        }
        playEffect('ui_click')
        this.persistDraft(this.latestDraftSnapshot || this.state.draftSnapshot || {}, 1)
        this.update({ step: 1, error: null })
      },

      prevStep() {
        playEffect('ui_click')
        this.persistDraft(this.latestDraftSnapshot || this.state.draftSnapshot || {}, 0)
        this.update({ step: 0, error: null })
        requestAnimationFrame(() => {
          this.applyDraftSnapshotToForm(this.state.draftSnapshot)
          this.refreshComposerState()
        })
      },

      worldSeedLabel(seed) {
        const firstLine = String(seed ?? '').split('\n')[0].trim()
        if (!firstLine) return 'UNTITLED WORLD'
        if (firstLine.length <= 72) return firstLine
        return `${firstLine.slice(0, 72)}...`
      },

      worldTimestamp(ts) {
        const parsed = Number(ts ?? 0)
        if (!Number.isFinite(parsed) || parsed <= 0) return 'Unknown date'
        return new Date(parsed).toLocaleString()
      },

      refreshComposerState() {
        const values = this.readDraftFormValues()
        const world = WorldDefinitionModel.fromValues(values?.world)
        const name = String(values?.name ?? '').trim()
        const identity = String(values?.identity ?? '').trim()
        const lineage = String(values?.lineage ?? '').trim()
        const vesselSummary = [name, identity, lineage && `(${lineage})`].filter(Boolean).join(' · ') || 'Vessel details not defined yet.'
        const worldSummary = this.state.importedSeed
          ? this.worldSeedLabel(this.state.importedSeed)
          : world.summary()
        this.update({ worldSummary, vesselSummary })
      },

      worldSourceView() {
        return {
          importedSeed: this.state.importedSeed,
          forging: this.state.forging,
          loading: this.state.galleryLoading,
          error: this.state.galleryError,
          cards: (this.state.worldGallery || []).filter(world => world?.seed).map(world => ({
            ...world,
            seedLabel: this.worldSeedLabel(world.seed),
            timestampLabel: this.worldTimestamp(world.timestamp),
          })),
          selectedWorldSaveId: this.state.selectedWorldSaveId,
          selectingWorldId: this.state.selectingWorldId,
          worldDefinitionOpen: this.state.worldDefinitionOpen,
          collapsed: false,
          active: true,
          summary: this.state.worldSummary,
        }
      },

      worldSourceActions() {
        return {
          chooseFreshWorld: this.chooseFreshWorld,
          chooseWorldFromGallery: this.chooseWorldFromGallery,
          toggleWorldDefinition: this.toggleWorldDefinition,
          activateSurface: () => {}, // NOP in wizard mode
        }
      },

      vesselView() {
        return {
          importedPlayer: this.state.importedPlayer,
          collapsed: false,
          active: true,
          summary: this.state.vesselSummary,
        }
      },

      vesselActions() {
        return {
          activateSurface: () => {}, // NOP in wizard mode
        }
      },

      async loadWorldGallery() {
        this.update({ galleryLoading: true, galleryError: null })
        try {
          const worldGallery = (await persistenceService.listWorlds()).filter(world => world?.seed)
          const importedSeed = String((gameStore.importedWorldData || {}).seed || '').trim() || null
          const selectedBySeed = importedSeed
            ? (worldGallery.find(world => world.seed === importedSeed) || {}).saveId || null
            : null
          this.update({
            worldGallery,
            selectedWorldSaveId: selectedBySeed,
            galleryLoading: false,
          })
          this.persistDraft()
          this.refreshComposerState()
        } catch (e) {
          console.error(e)
          this.update({
            galleryError: 'Could not load saved worlds.',
            galleryLoading: false,
          })
        }
      },

      chooseFreshWorld() {
        if (this.state.forging || this.state.selectingWorldId) return
        gameStore.setImportedWorldData(null)
        this.syncImportedSetup()
        this.update({ selectedWorldSaveId: null, error: null, worldDefinitionOpen: true, activeSurface: 'world' })
        this.persistDraft()
        this.focusWorldDefinition()
      },

      toggleWorldDefinition() {
        if (this.state.importedSeed) return
        const worldDefinitionOpen = !this.state.worldDefinitionOpen
        this.update({ worldDefinitionOpen })
        if (worldDefinitionOpen) this.focusWorldDefinition()
      },

      async chooseWorldFromGallery(e) {
        const saveId = String(e?.currentTarget?.dataset?.saveId ?? '').trim()
        if (!saveId || this.state.forging || this.state.selectingWorldId) return

        if (!this.state.importedSeed && !WorldDefinitionModel.fromValues(this.state.draftSnapshot?.world).isEmpty()) {
          if (!window.confirm('Importing this world will overwrite your current world draft. Continue?')) {
            return
          }
        }

        this.update({ selectingWorldId: saveId, error: null })
        try {
          const selected = await gameStore.importWorldFromSave(saveId)
          if (!selected) throw new Error('Save unavailable')
          this.syncImportedSetup()
          this.update({ selectedWorldSaveId: saveId, selectingWorldId: null, worldDefinitionOpen: false, step: 1 })
          this.persistDraft()
          this.refreshComposerState()
          sysNotify('World loaded from gallery.')
        } catch (err) {
          console.error(err)
          this.update({
            selectingWorldId: null,
            error: 'Failed to load this world. Try another save.',
          })
        }
      },

      forgeLabel() {
        if (this.state.importedSeed && this.state.importedPlayer) return 'INITIATE SHARED STARTER'
        if (this.state.importedSeed) return 'ROLL VESSEL IN SHARED WORLD'
        if (this.state.importedPlayer) return 'INITIATE WORLD FOR IMPORTED VESSEL'
        return 'INITIATE REALITY FORGE'
      },

      focusWorldDefinition() {
        requestAnimationFrame(() => {
          const el = this.$('[name="world.inspiration"]')
          el?.focus?.()
          el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
        })
      },

      backToLobby() {
        this.persistDraft()
        navigateState('hub')
      },

      cancel() {
        this.clearDraft()
        gameStore.clearImportedWorldData()
        navigateState('hub')
      },

      async settleFocusBeforeTransition() {
        const active = document.activeElement
        if (active instanceof HTMLElement) active.blur()
        await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)))
      },

      async startNewGame(e) {
        e?.preventDefault?.()
        this.update({ showErrors: true })
        const formNode = this.getFormNode()
        const values = collectValues(formNode, undefined, 'name')
        
        if (values._errors && Object.keys(values._errors).length > 0) {
          this.update({ error: 'Please fix the errors in your vessel definition.' })
          return
        }

        const { name = '', identity, lineage = '', sex = '', bio } = values
        const worldData = {
          inspiration: values['world.inspiration'],
          modifiers: values['world.modifiers'],
          narrativeType: values['world.narrativeType'],
        }
        const worldModel = WorldDefinitionModel.fromValues(worldData)
        const seed = this.state.importedSeed ? '' : worldModel.toSeed()

        await this.settleFocusBeforeTransition()
        this.update({ forging: true, error: null, showErrors: false })
        sysNotify('Forging reality...')
        
        try {
          const result = await gameStore.startNewGame({
            name, identity, lineage, sex, bio, seed,
            worldData: gameStore.importedWorldData,
            playerData: gameStore.importedPlayerData,
          })
          this.clearDraft()
          const saveId = String(result?.saveId || gameStore.activeSaveId || '').trim()
          if (!saveId) throw new Error('Active save id missing after forge')

          await this.settleFocusBeforeTransition()
          await navigateState('scene', saveId)
        } catch (e) {
          console.error('[NewGameWizard] Forge failed:', e)
          const message = formatAIErrorMessage(e)
          this.update({ error: message, forging: false })
          sysNotify(message)
        }
      },
    }
  </script>

  <style>
    :host {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: var(--space-md);
    }

    .menu-section {
      width: 100%;
      max-width: 48rem;
      display: flex;
      flex-direction: column;
      gap: var(--space-lg);
    }

    .wizard-form {
      display: grid;
      gap: var(--space-lg);
    }

    .wizard-steps {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--space-md);
      margin-bottom: var(--space-sm);
    }

    .wizard-step-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--space-xs);
      opacity: 0.4;
      transition: opacity 0.24s ease;
    }

    .wizard-step-item.active { opacity: 1; }
    .wizard-step-item.done { opacity: 0.8; color: var(--sem-success-fg); }

    .step-num {
      width: 2rem;
      height: 2rem;
      border-radius: 50%;
      border: 0.125rem solid currentColor;
      display: grid;
      place-items: center;
      font-weight: bold;
      font-size: var(--type-sm);
    }

    .step-label {
      font-size: var(--type-3xs);
      letter-spacing: 0.15em;
      font-weight: bold;
    }

    .wizard-step-sep {
      width: 3rem;
      height: 1px;
      background: var(--border);
      margin-top: -1rem;
    }

    .wizard-body {
      min-height: 24rem;
    }

    .wizard-footer-nav {
      display: flex;
      justify-content: space-between;
      gap: var(--space-md);
      padding-top: var(--space-lg);
      border-top: 1px solid var(--border);
    }

    .wizard-actions-footer {
      display: flex;
      justify-content: center;
      gap: var(--space-lg);
      margin-top: var(--space-md);
    }

    .wizard-import-note {
      text-align: center;
      font-size: var(--type-2xs);
      letter-spacing: 0.1em;
      color: var(--gold);
      font-weight: bold;
      margin: 0;
    }

    .wizard-error-note {
      color: var(--danger);
      font-size: var(--type-xs);
      text-align: center;
      margin: 0;
      animation: shake 0.4s ease;
    }

    .nav-link {
      font-size: var(--type-2xs);
      letter-spacing: 0.12em;
      font-weight: bold;
      color: var(--text-dim);
      text-transform: uppercase;
      cursor: pointer;
      &:hover { color: var(--text-prose); }
    }

    .danger-link {
      color: color-mix(in srgb, var(--danger) 60%, var(--text-dim));
      &:hover { color: var(--danger); }
    }

    @media (max-width: 45rem) {
      .wizard-steps { gap: var(--space-sm); }
      .wizard-step-sep { width: 1.5rem; }
      .step-label { font-size: var(--type-3xs); }
    }
  </style>
</forging-view>
